import { startTransition, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../Button/Button";
import Typography from "../Typography/Typography";
import Loader from "../Loader/Loader";
import styles from "./RequestForm.module.scss";
import infoIconSvg from "../../../src/assets/images/UserTable/InfoIcon.svg";
import arrowDropDownSvg from "../../../src/assets/arrow_drop_down.svg";
import searchIcon from "../../../src/assets/search.svg";
import { ROUTES } from "../../common/constants";
import { Box, Breadcrumbs, Link } from "@mui/material";
import BackArrowSvg from "../../../src/assets/images/linkArrowLeft.svg";
import genAIEditIcon from "../../../src/assets/Pen.svg";
import closeIconSvg from "../../../src/assets/cross_dark.svg";
import AstroIcon from "../../../src/assets/AskAstroOrionTheme.svg";
import { getToast } from "../../common/utils";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { WorkBenchTableEntity } from "../../modules/ManageContract/model";
import { updateWorkbenchData } from "../../store/thunks/table.thunk";
import { generateRequirementsDocument } from "../../services/agent.service";

const RequestForm = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get finalJsonData from Redux
  const { finalJsonData } = useAppSelector(
    (store) => store?.rootReducer?.askOrionReducer
  );

  const [requestName, setRequestName] = useState("");
  const [description, setDescription] = useState("");
  const [totalContractValue, setTotalContractValue] = useState("2,000,000");
  const [owner, setOwner] = useState("");
  const [buildingAddress, setBuildingAddress] = useState("");
  const [clearanceLevel, setClearanceLevel] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [descriptionError, setDescriptionError] = useState("");

  // Helper to count words in the description
  const countWords = (text: string) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const wordCount = useMemo(() => countWords(description), [description]);

  // Populate form fields from Redux data when component mounts or data changes
  useEffect(() => {
    if (finalJsonData) {
      setRequestName(finalJsonData.sourcing_request_title || "");
      setDescription(finalJsonData.short_description || "");
      setBuildingAddress(finalJsonData.building_address || "");
      setClearanceLevel(finalJsonData.clearance_level || "");
      setSelectedTemplate(finalJsonData.template_type || "");
    }
  }, [finalJsonData]);

  const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({
    baseLanguage: false,
    regions: false,
    departments: false,
    resourcedBy: false,
    projectReason: false,
    currency: false,
    commodity: false,
    executionStrategy: false,
    template: false,
    targetSavings: false,
    sourcingRequestType: false,
  });
  const dropdownRefs = {
    baseLanguage: useRef<HTMLDivElement>(null),
    regions: useRef<HTMLDivElement>(null),
    departments: useRef<HTMLDivElement>(null),
    resourcedBy: useRef<HTMLDivElement>(null),
    projectReason: useRef<HTMLDivElement>(null),
    currency: useRef<HTMLDivElement>(null),
    commodity: useRef<HTMLDivElement>(null),
    executionStrategy: useRef<HTMLDivElement>(null),
    template: useRef<HTMLDivElement>(null),
    targetSavings: useRef<HTMLDivElement>(null),
    sourcingRequestType: useRef<HTMLDivElement>(null),
  };

  const handleSubmit = async () => {
    // Validate required fields (only visible fields)
    if (!requestName || !selectedTemplate) {
      getToast("Error", "Please fill in all required fields");
      return;
    }

    // Validate description word count
    if (wordCount > 1000) {
      getToast("Error", "Description cannot exceed 1000 words");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Call the API to generate requirements document
      // buildingAddress and clearanceLevel come from Redux state (populated by chatbot)
      const response = await generateRequirementsDocument({
        sourcing_request_title: requestName,
        building_address: buildingAddress || "",
        clearance_level: clearanceLevel || "",
        template_type: selectedTemplate,
        short_description: description || "",
        total_contract_value: totalContractValue || "",
      });

      if (response.status) {
        // Create new entry with document links and doc_id
        const newEntry = new WorkBenchTableEntity({
          request: requestName,
          status: "submitted_for_approval",
          budget: `$${totalContractValue}`,
          categorymanager: owner,
          document_drive_link: response.document_drive_link,
          document_gcs_path: response.document_gcs_path,
          doc_id: response.doc_id,
        });
        
        dispatch(updateWorkbenchData(newEntry));
        
        getToast("Success", "Request Submitted Successfully!");
        
        startTransition(() => {
          navigate(ROUTES.workbenchDashboard);
        });
      } else {
        getToast("Error", response.status_message || "Failed to generate document");
      }
    } catch (error: any) {
      console.error("Error submitting request:", error);
      getToast("Error", error?.response?.data?.status_message || "Failed to submit request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = () => {
    const newEntry = new WorkBenchTableEntity({
      request: requestName,
      status: "draft",
      budget: `$${totalContractValue}`,
      categorymanager: owner,
    });
    dispatch(updateWorkbenchData(newEntry));
    startTransition(() => {
      navigate(ROUTES.workbenchDashboard);
    });

    getToast("Success", "Draft saved successfully!");
  };

  const toggleDropdown = (name: string) => {
    setDropdownOpen((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const handleClickOutside = (event: MouseEvent) => {
    Object.keys(dropdownRefs).forEach((key) => {
      const ref = dropdownRefs[key as keyof typeof dropdownRefs];
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setDropdownOpen((prevState) => ({
          ...prevState,
          [key]: false,
        }));
      }
    });
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle contract value input with $ formatting
  const handleContractValueChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Remove any non-numeric characters except commas
    const value = e.target.value.replace(/[^0-9,]/g, "");
    setTotalContractValue(value);
  };

  // Handle description changes with word limit validation
  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setDescription(value);
    const currentWordCount = countWords(value);
    if (currentWordCount > 1000) {
      setDescriptionError("Description cannot exceed 1000 words");
    } else {
      setDescriptionError("");
    }
  };

  const dropdownItems = {
    baseLanguage: [
      { key: "1", label: "English" },
      { key: "2", label: "Spanish" },
      { key: "3", label: "French" },
      { key: "4", label: "German" },
    ],
    regions: [
      { key: "1", label: "North America" },
      { key: "2", label: "Europe" },
      { key: "3", label: "Asia-Pacific" },
      { key: "4", label: "Latin America" },
    ],
    departments: [
      { key: "1", label: "Marketing" },
      { key: "2", label: "Sales" },
      { key: "3", label: "Finance" },
      { key: "4", label: "Operations" },
    ],
    resourcedBy: [
      { key: "1", label: "Agency Procurement Organization" },
      { key: "2", label: "GSA AAS" },
      { key: "3", label: "GSA OCAS" },
      { key: "4", label: "Shared Service" },
      { key: "5", label: "TBD " },
    ],
    projectReason: [
      { key: "1", label: "New Requirement" },
      { key: "2", label: "Renewal" },
      { key: "3", label: "Expansion" },
      { key: "4", label: "Compliance" },
    ],
    owner: [
      { key: "1", label: "Morgan Lee" },
      { key: "2", label: "Alex Kim" },
      { key: "3", label: "Jordan Smith" },
      { key: "4", label: "Taylor Brown" },
    ],
    currency: [
      { key: "1", label: "USD" },
      { key: "2", label: "EUR" },
      { key: "3", label: "GBP" },
      { key: "4", label: "INR" },
    ],
    commodity: [
      { key: "1", label: "Marketing" },
      { key: "2", label: "IT Services" },
      { key: "3", label: "Consulting" },
      { key: "4", label: "Facilities" },
    ],
    executionStrategy: [
      { key: "1", label: "Tactical Sourcing" },
      { key: "2", label: "Spot Buy" },
      { key: "3", label: "Long-term Contract" },
    ],
    template: [
      { key: "1", label: "ID/IQ task order" },
      { key: "2", label: "Shared service IAA" },
      { key: "3", label: "General Template" },
      { key: "4", label: "Category Management Recommended" },
    ],
    sourcingRequestType: [
      { key: "1", label: "Category Sourcing Request" },
      { key: "2", label: "Spot Buy Request" },
      { key: "3", label: "Contract Renewal" },
      { key: "4", label: "Consulting Engagement" },
    ],
  };

  const [focusedDropdown, setFocusedDropdown] = useState<string | null>(null);

  // State for Procured By (resourcedBy)
  const [resourcedBy, setResourcedBy] = useState(
    "Agency Procurement Organization"
  );
  const [executionStrategy, setExecutionStrategy] = useState(
    "Tactical Sourcing"
  );

  // Handler for selecting a dropdown item for Procured By
  const handleResourcedBySelect = (label: string) => {
    setResourcedBy(label);
    setDropdownOpen((prev) => ({ ...prev, resourcedBy: false }));
  };

  const handleExecutionStrategyBySelect = (label: string) => {
    setExecutionStrategy(label);
    setDropdownOpen((prev) => ({ ...prev, executionStrategy: false }));
  };

  const handleTemplateSelect = (label: string) => {
    setSelectedTemplate(label);
    setDropdownOpen((prev) => ({ ...prev, template: false }));
  };
  return (
    <div className={styles.requestFormContainer}>
      <div className={styles.breadcrumb}>
        <Breadcrumbs
          aria-label="breadcrumb"
          style={{ fontSize: "14px", fontWeight: "400" }}
        >
          <Link
            underline="hover"
            color="#3474E0"
            href={ROUTES.workbenchDashboard}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <img src={BackArrowSvg} alt="" />
              Home
            </Box>
          </Link>
          <Typography color="#282B31" fontSize={14} fontWeight={500}>
            {requestName || "Untitled"} - Draft
          </Typography>
        </Breadcrumbs>
        <img src={AstroIcon} alt="Astro" className={styles.astroIcon} />
      </div>

      <div className={styles.formContainer}>
        <div className={styles.header}>
          <div className={styles.inputGroup}>
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Request Name
              <span className={styles.requiredAsterisk}>*</span>
            </Typography>
            <input
              type="text"
              value={requestName}
              onChange={(e) => setRequestName(e.target.value)}
              placeholder="Input here"
              className={`${styles.input} ${styles.buttonStyle}`}
              style={{
                fontFamily: "Google Sans",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0%",
                color: "#282B31",
              }}
            />
          </div>
          <span className={styles.infoText}>
            <img src={infoIconSvg} alt="info" className={styles.infoIcon} />
            This draft is created with help of AI. Please review once before
            submitting request.
          </span>
          <div className={styles.buttonGroup}>
            {/* Save Draft button hidden as per requirement */}
            {/* <Button
              onClick={(e) => { e.preventDefault(); }}
              variant="outlined"
              style={{
                borderRadius: "24px",
                padding: "4px 16px",
                fontSize: "12px",
              }}
            >
              Save Draft
            </Button> */}
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting}
              style={{
                borderRadius: "24px",
                padding: "4px 16px",
                fontSize: "12px",
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </div>
        <div className={styles.description}>
          <div className={styles.descriptionHeader}>
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ fontSize: "12px" }}
            >
              Description
            </Typography>
            <img src={infoIconSvg} alt="info" className={styles.infoIcon} />
            <div className={styles.textCount}>
              <span style={{ color: wordCount > 1000 ? "#D32F2F" : "#7A7F89" }}>
                {wordCount}/1000
              </span>
              {/* Pen/Edit icon hidden as per requirement */}
              {/* <img src={genAIEditIcon} alt="Edit" className={styles.editIcon} /> */}
            </div>
          </div>
          <textarea
            value={description}
            onChange={handleDescriptionChange}
            className={styles.textarea}
            rows={4}
            placeholder="Enter description..."
            style={{
              fontFamily: "Google Sans",
              fontWeight: 400,
              fontSize: "12px",
              letterSpacing: "0%",
              color: "#282B31",
              border: descriptionError ? "1px solid #D32F2F" : undefined,
            }}
          />
          {descriptionError && (
            <Typography
              variant="body2"
              style={{
                fontSize: "12px",
                color: "#D32F2F",
                marginTop: "4px",
              }}
            >
              {descriptionError}
            </Typography>
          )}
        </div>
        <div className={styles.divider}></div>
        {/* Copied details block removed as requested */}
        <div className={styles.row}>
          <div
            className={styles.dropdownContainer}
            ref={dropdownRefs.baseLanguage}
          >
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Base Language
              <span className={styles.requiredAsterisk}>*</span>
            </Typography>
            <button
              onClick={() => toggleDropdown("baseLanguage")}
              onFocus={() => setFocusedDropdown("baseLanguage")}
              onBlur={() => setFocusedDropdown(null)}
              className={`${styles.dropdownButton} ${
                focusedDropdown === "baseLanguage" ? styles.inputFocus : ""
              }`}
              style={{ backgroundColor: "#F2F4F7", cursor: "not-allowed" }}
              disabled
              aria-disabled="true"
            >
              <Typography
                variant="body2"
                color="#282B31"
                style={{ textAlign: "left", fontSize: "12px" }}
              >
                English
              </Typography>
              <img
                src={dropdownOpen.baseLanguage ? searchIcon : arrowDropDownSvg}
                alt="Arrow"
                className={styles.dropdownArrow}
              />
            </button>
            {dropdownOpen.baseLanguage && (
              <div className={styles.dropdownContent}>
                {dropdownItems.baseLanguage.map((item) => (
                  <div key={item.key} className={styles.dropdownItem}>
                    <Typography variant="body2" color="#282B31">
                      {item.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className={styles.dropdownContainer}
            ref={dropdownRefs.departments}
          >
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Departments
            </Typography>
            <button
              onClick={() => toggleDropdown("departments")}
              onFocus={() => setFocusedDropdown("departments")}
              onBlur={() => setFocusedDropdown(null)}
              className={`${styles.dropdownButton} ${
                focusedDropdown === "departments" ? styles.inputFocus : ""
              }`}
              style={{ backgroundColor: "#F2F4F7", cursor: "not-allowed" }}
              disabled
              aria-disabled="true"
            >
              <div className={styles.tag}>
                <Typography
                  variant="body2"
                  color="#282B31"
                  style={{ fontSize: "12px" }}
                >
                  All
                </Typography>
                <img
                  src={closeIconSvg}
                  alt="Close"
                  className={styles.closeIcon}
                />
              </div>
              <img
                src={dropdownOpen.departments ? searchIcon : arrowDropDownSvg}
                alt="Arrow"
                className={styles.dropdownArrow}
              />
            </button>
            {dropdownOpen.departments && (
              <div className={styles.dropdownContent}>
                {dropdownItems.departments.map((item) => (
                  <div key={item.key} className={styles.dropdownItem}>
                    <Typography variant="body2" color="#282B31">
                      {item.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className={styles.dropdownContainer}
            ref={dropdownRefs.resourcedBy}
          >
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Procured By
            </Typography>
            <button
              onClick={() => toggleDropdown("resourcedBy")}
              onFocus={() => setFocusedDropdown("resourcedBy")}
              onBlur={() => setFocusedDropdown(null)}
              className={`${styles.dropdownButton} ${
                focusedDropdown === "resourcedBy" ? styles.inputFocus : ""
              }`}
            >
              <Typography
                variant="body2"
                color="#282B31"
                style={{ textAlign: "left", fontSize: "12px" }}
              >
                {resourcedBy}
              </Typography>
              <img
                src={dropdownOpen.resourcedBy ? searchIcon : arrowDropDownSvg}
                alt="Arrow"
                className={styles.dropdownArrow}
              />
            </button>
            {dropdownOpen.resourcedBy && (
              <div className={styles.dropdownContent}>
                {dropdownItems.resourcedBy.map((item) => (
                  <div
                    key={item.key}
                    className={styles.dropdownItem}
                    onClick={() => handleResourcedBySelect(item.label)}
                    style={{ cursor: "pointer" }}
                  >
                    <Typography variant="body2" color="#282B31">
                      {item.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.row}>
          {/* Changed from dropdown to input field with $ formatting */}
          <div className={styles.inputGrouptc}>
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Total Contract Value
            </Typography>
            <div className={styles.inputWithPrefixtc}>
              <span className={styles.currencyPrefixtc}>$</span>
              <input
                type="text"
                value={totalContractValue}
                onChange={handleContractValueChange}
                placeholder="Enter value"
                className={`${styles.inputtc} ${styles.buttonStyletc}`}
                style={{
                  fontFamily: "Google Sans",
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0%",
                  color: "#282B31",
                  paddingLeft: "18px",
                }}
              />
            </div>
          </div>

          <div
            className={styles.dropdownContainer}
            ref={dropdownRefs.projectReason}
          >
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Project Reason
            </Typography>
            <button
              onClick={() => toggleDropdown("projectReason")}
              onFocus={() => setFocusedDropdown("projectReason")}
              onBlur={() => setFocusedDropdown(null)}
              className={`${styles.dropdownButton} ${
                focusedDropdown === "projectReason" ? styles.inputFocus : ""
              }`}
              style={{ backgroundColor: "#F2F4F7", cursor: "not-allowed" }}
              disabled
              aria-disabled="true"
            >
              <Typography
                variant="body2"
                color="#282B31"
                style={{ textAlign: "left", fontSize: "12px" }}
              >
                New Requirement
              </Typography>
              <img
                src={dropdownOpen.projectReason ? searchIcon : arrowDropDownSvg}
                alt="Arrow"
                className={styles.dropdownArrow}
              />
            </button>
            {dropdownOpen.projectReason && (
              <div className={styles.dropdownContent}>
                {dropdownItems.projectReason.map((item) => (
                  <div key={item.key} className={styles.dropdownItem}>
                    <Typography variant="body2" color="#282B31">
                      {item.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Changed from dropdown to input field */}
          <div className={styles.inputGrouptc}>
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Owner
            </Typography>
            <input
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder="Enter owner name"
              className={`${styles.inputtc} ${styles.buttonStyletc}`}
              style={{
                fontFamily: "Google Sans",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0%",
                color: "#282B31",
              }}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.dropdownContainer} ref={dropdownRefs.currency}>
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Currency
            </Typography>
            <button
              onClick={() => toggleDropdown("currency")}
              onFocus={() => setFocusedDropdown("currency")}
              onBlur={() => setFocusedDropdown(null)}
              className={`${styles.dropdownButton} ${
                focusedDropdown === "currency" ? styles.inputFocus : ""
              }`}
              style={{ backgroundColor: "#F2F4F7", cursor: "not-allowed" }}
              disabled
              aria-disabled="true"
            >
              <Typography
                variant="body2"
                color="#282B31"
                style={{ textAlign: "left", fontSize: "12px" }}
              >
                USD
              </Typography>
              <img
                src={dropdownOpen.currency ? searchIcon : arrowDropDownSvg}
                alt="Arrow"
                className={styles.dropdownArrow}
              />
            </button>
            {dropdownOpen.currency && (
              <div className={styles.dropdownContent}>
                {dropdownItems.currency.map((item) => (
                  <div key={item.key} className={styles.dropdownItem}>
                    <Typography variant="body2" color="#282B31">
                      {item.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className={styles.dropdownContainer}
            ref={dropdownRefs.commodity}
          >
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Category
            </Typography>
            <button
              onClick={() => toggleDropdown("commodity")}
              onFocus={() => setFocusedDropdown("commodity")}
              onBlur={() => setFocusedDropdown(null)}
              className={`${styles.dropdownButton} ${
                focusedDropdown === "commodity" ? styles.inputFocus : ""
              }`}
              style={{ backgroundColor: "#F2F4F7", cursor: "not-allowed" }}
              disabled
              aria-disabled="true"
            >
              <Typography
                variant="body2"
                color="#282B31"
                style={{ textAlign: "left", fontSize: "12px" }}
              >
                Facilities
              </Typography>
              <img
                src={dropdownOpen.commodity ? searchIcon : arrowDropDownSvg}
                alt="Arrow"
                className={styles.dropdownArrow}
              />
            </button>
            {dropdownOpen.commodity && (
              <div className={styles.dropdownContent}>
                {dropdownItems.commodity.map((item) => (
                  <div key={item.key} className={styles.dropdownItem}>
                    <Typography variant="body2" color="#282B31">
                      {item.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className={styles.dropdownContainer}
            ref={dropdownRefs.executionStrategy}
          >
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Execution Strategy
            </Typography>
            <button
              onClick={() => toggleDropdown("executionStrategy")}
              onFocus={() => setFocusedDropdown("executionStrategy")}
              onBlur={() => setFocusedDropdown(null)}
              className={`${styles.dropdownButton} ${
                focusedDropdown === "executionStrategy" ? styles.inputFocus : ""
              }`}
            >
              <Typography
                variant="body2"
                color="#282B31"
                style={{ textAlign: "left", fontSize: "12px" }}
              >
                {executionStrategy}
              </Typography>
              <img
                src={
                  dropdownOpen.executionStrategy ? searchIcon : arrowDropDownSvg
                }
                alt="Arrow"
                className={styles.dropdownArrow}
              />
            </button>
            {dropdownOpen.executionStrategy && (
              <div className={styles.dropdownContent}>
                {dropdownItems.executionStrategy.map((item) => (
                  <div
                    key={item.key}
                    onClick={() => handleExecutionStrategyBySelect(item?.label)}
                    className={styles.dropdownItem}
                  >
                    <Typography variant="body2" color="#282B31">
                      {item.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className={styles.dropdownContainer}
            ref={dropdownRefs.sourcingRequestType}
          >
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ marginBottom: "4px", fontSize: "12px" }}
            >
              Sourcing Request Type
            </Typography>
            <button
              onClick={() => toggleDropdown("sourcingRequestType")}
              onFocus={() => setFocusedDropdown("sourcingRequestType")}
              onBlur={() => setFocusedDropdown(null)}
              className={`${styles.dropdownButton} ${
                focusedDropdown === "sourcingRequestType"
                  ? styles.inputFocus
                  : ""
              }`}
              style={{ backgroundColor: "#F2F4F7", cursor: "not-allowed" }}
              disabled
              aria-disabled="true"
            >
              <Typography
                variant="body2"
                color="#282B31"
                style={{ textAlign: "left", fontSize: "12px" }}
              >
                Category Sourcing Request
              </Typography>
              <img
                src={dropdownOpen.baseLanguage ? searchIcon : arrowDropDownSvg}
                alt="Arrow"
                className={styles.dropdownArrow}
              />
            </button>
            {dropdownOpen.sourcingRequestType && (
              <div className={styles.dropdownContent}>
                {dropdownItems.sourcingRequestType.map((item) => (
                  <div key={item.key} className={styles.dropdownItem}>
                    <Typography variant="body2" color="#282B31">
                      {item.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className={styles.checkboxContainer}>
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{ fontSize: "12px" }}
            >
              Is Global Clearance Required
            </Typography>
            <div className={styles.radioGroup}>
              <label>
                <input type="radio" name="globalClearance" value="yes" />
                Yes
              </label>
              <label>
                <input type="radio" name="globalClearance" value="no" />
                No
              </label>
            </div>
          </div>
        </div>
        <div className={styles.divider}></div>
        <div className={styles.row}>
          <div className={styles.dropdownContainer} ref={dropdownRefs.template}>
            <Typography
              variant="body2"
              color="#7A7F89"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "4px",
                fontSize: "12px",
              }}
            >
              Template
              <span className={styles.requiredAsterisk}>*</span>
              <img
                src={infoIconSvg}
                alt="info"
                className={styles.infoIcon}
                style={{ marginLeft: "4px" }}
              />
            </Typography>
            <button
              onClick={() => toggleDropdown("template")}
              onFocus={() => setFocusedDropdown("template")}
              onBlur={() => setFocusedDropdown(null)}
              className={`${styles.dropdownButton} ${
                focusedDropdown === "template" ? styles.inputFocus : ""
              }`}
            >
              <Typography
                variant="body2"
                color="#282B31"
                style={{ textAlign: "left", fontSize: "12px" }}
              >
                {selectedTemplate || "Select Template"}
              </Typography>
              <img
                src={dropdownOpen.template ? searchIcon : arrowDropDownSvg}
                alt="Arrow"
                className={styles.dropdownArrow}
              />
            </button>
            {dropdownOpen.template && (
              <div className={styles.dropdownContent}>
                {dropdownItems.template.map((item) => (
                  <div
                    key={item.key}
                    className={styles.dropdownItem}
                    onClick={() => handleTemplateSelect(item.label)}
                    style={{ cursor: "pointer" }}
                  >
                    <Typography variant="body2" color="#282B31">
                      {item.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full-page overlay loader */}
      {isSubmitting && (
        <div className={styles.loaderOverlay}>
          <Loader 
            loaderText="Submitting your request..." 
            loaderColor="#FFFFFF" 
            textColor="#FFFFFF"
            secondaryText={"This will take 1-2 minutes for generating the document"}
          />
        </div>
      )}
    </div>
  );
};

export default RequestForm;
